import React, { useState } from 'react';
import { Alert, App, ConfigProvider } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import usePrompt from '../../../hooks/common/usePrompt';
import Tabs from '../../../components/Tabs';
import { SettingsWorkspaceStyle } from '../common/styles';
import { PayMethodExhibitPanel } from './PayMethodExhibitTable/PayMethodExhibitPanel';
import { RoutePanel } from './RoutePanel/RoutePanel';
import { PaymentSettingLogPanel } from './PaymentSettingLogPanel/PaymentSettingLogPanel';

type PaymentSettingsWorkspaceProps = { canUpdate: boolean };
const TAB_KEYS = ['methods', 'routing', 'history'] as const;

export const PaymentSettingsWorkspace = (props: PaymentSettingsWorkspaceProps): React.JSX.Element => {
  const { canUpdate } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab = requestedTab === 'routing' || requestedTab === 'history' ? requestedTab : 'methods';
  const [isMethodsDirty, setMethodsDirty] = useState(false);
  const [isRoutesDirty, setRoutesDirty] = useState(false);
  usePrompt(isMethodsDirty || isRoutesDirty, '저장하지 않은 결제 설정이 있습니다. 이 페이지를 나가시겠습니까?');

  const tabLabel = (text: string, isDirty: boolean): React.JSX.Element => (
    <span className="settings-tab-label">
      {text}
      {isDirty && <span className="settings-unsaved" role="img" aria-label="저장하지 않은 변경" />}
    </span>
  );

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#7950f2',
          borderRadius: 4,
          colorText: '#283141',
          colorTextSecondary: '#626e7f',
          controlHeight: 36,
          controlHeightSM: 32,
        },
        components: {
          Button: { fontWeight: 500, paddingInline: 16, paddingInlineSM: 12 },
          Switch: { trackHeight: 24, trackMinWidth: 44, handleSize: 20 },
        },
      }}
    >
      <App>
        <DndProvider backend={HTML5Backend}>
          <SettingsWorkspaceStyle>
            {!canUpdate && (
              <Alert
                className="settings-inline-alert"
                type="info"
                showIcon
                message="조회 권한으로 보고 있습니다. 설정 변경에는 수정 권한이 필요합니다."
              />
            )}
            <Tabs
              activeKey={TAB_KEYS.indexOf(activeTab)}
              onChange={(key: number) =>
                setSearchParams(
                  (previous) => {
                    const next = new URLSearchParams(previous);
                    next.set('tab', TAB_KEYS[key] ?? 'methods');
                    return next;
                  },
                  { replace: true },
                )
              }
              destroyInactiveTabPane={false}
              list={[
                {
                  id: 0,
                  title: tabLabel('결제수단·혜택', isMethodsDirty),
                  forceRender: true,
                  content: <PayMethodExhibitPanel canUpdate={canUpdate} onDirtyChange={setMethodsDirty} />,
                },
                {
                  id: 1,
                  title: tabLabel('PG 라우팅', isRoutesDirty),
                  forceRender: true,
                  content: <RoutePanel canUpdate={canUpdate} onDirtyChange={setRoutesDirty} />,
                },
                {
                  id: 2,
                  title: '변경 이력',
                  content: activeTab === 'history' ? <PaymentSettingLogPanel /> : null,
                },
              ]}
            />
          </SettingsWorkspaceStyle>
        </DndProvider>
      </App>
    </ConfigProvider>
  );
};
